// The engine: board → logical kinds → a kit's rules → frame stacks per cell (#166; 12 §8 T9).
import { boardSeed, cellHash, inPatch } from './hash';
import { DIRS, firstRule, mask8, sample, type EdgePolicy, type KindGrid } from './grid';
import { frame } from './oryx';
import { groundFill, groundInward, groundLips, liquidFill, pickVariant, shoreOverlays, wallPiece } from './patterns';
import type { BoardLike, DecalRule, Kind, Kit, LogicalCell, TiledBoard, Variants, ViewCell, ViewFrame } from './types';
import { FILLS, PROPS, SHADOW } from './oryx';

/** Default thicket brush and the low clump drawn under it (docs/26). */
const THICKET: Variants = [[PROPS.bushDense[0], 5], [PROPS.roundBush[0], 2], [PROPS.roundBush[1], 2]];
const THICKET_UNDER: Variants = [[PROPS.bushSmall[0], 1], [PROPS.bushSmall[1], 1], [PROPS.bushSmall[2], 1], [PROPS.shrubs.green[1], 1]];

const MIRROR: EdgePolicy = { mode: 'mirror' };
const AS_ROCK: EdgePolicy = { mode: 'as', kind: 'rock' };
const NIL_EDGE: EdgePolicy = { mode: 'nil' };

/** Terrain → kind. `Wall` becomes `rock` when nothing but wall surrounds it (off-map and void count as wall). */
export function classify(board: BoardLike): LogicalCell[] {
  const { w, h, tiles } = board;
  const isWall = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= w || y >= h) return true;
    const t = tiles[y * w + x];
    return !t || t.terrain === 'Wall' || t.terrain === 'Void';
  };
  const out: LogicalCell[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const t = tiles[y * w + x] ?? { terrain: 'Wall' as const, elev: 0, cover: 0, opaque: true };
      let kind: Kind;
      switch (t.terrain) {
        case 'Wall': kind = DIRS.every((d) => isWall(x + d.dx, y + d.dy)) ? 'rock' : 'wall'; break;
        case 'Water': kind = 'water'; break;
        case 'Bad': kind = 'rough'; break;
        case 'Door': kind = t.veil || (t.way && t.doorOpen) ? 'floor' : 'door'; break;
        case 'Tunnel': kind = 'tunnel'; break;
        case 'Deep': kind = 'deep'; break;
        case 'Thicket': kind = 'thicket'; break;
        case 'Chasm': kind = 'chasm'; break;
        case 'Void': kind = 'void'; break;
        default: kind = 'floor';
      }
      out.push({ kind, tile: t });
    }
  }
  return out;
}

/** Posing (#166 "Poser tiles"): `wall` also matches rock and doors; `ground` matches floor. */
function poses(kind: string, as: string): boolean {
  if (kind === as) return true;
  if (as === 'wall') return kind === 'rock' || kind === 'door';
  if (as === 'solid') return kind === 'wall' || kind === 'rock' || kind === 'door';
  if (as === 'open') return kind === 'floor' || kind === 'rough' || kind === 'water' || kind === 'thicket';
  if (as === 'water') return kind === 'deep';
  return false;
}

const vf = (i: number): ViewFrame => ({ frame: frame(i) });
const vanim = (a: number, b: number): ViewFrame => ({ frame: frame(a), anim: [frame(a), frame(b)] });

export interface TileOptions {
  doorOpen?: (id: number) => boolean;
  /** View seed; overrides `board.seed`. Without either, the board's tiles are hashed. */
  seed?: number;
}

/**
 * Board → frame stacks. Every choice is a pure function of (kit, seed, x, y, neighbourhood), so a
 * party of four sees one picture: the seed salts the per-cell hash and the seeded patches that
 * cluster decoration (meadows, drifts, groves).
 */
export function tileBoard(board: BoardLike, kit: Kit, options: TileOptions = {}): TiledBoard {
  const cells = classify(board);
  const { w, h } = board;
  const seed = options.seed ?? board.seed ?? boardSeed(board);
  const salt = `${kit.key}#${seed >>> 0}`;
  // a void square samples as off the map under each query's own edge policy (grid.ts `sample`)
  const grid: KindGrid = { w, h, kindAt: (x, y) => cells[y * w + x]!.kind };
  const kindIs = (k: Kind | readonly Kind[]) => (s: string): boolean => (typeof k === 'string' ? s === k : (k as readonly string[]).includes(s));
  // a thicket stands on the ground like the floor around it (docs/26)
  const groundKinds: readonly Kind[] = kit.ground ? [...kit.ground.on, ...(kit.ground.on.includes('floor') ? ['thicket' as const] : [])] : [];
  const groundEdges = kit.ground?.edges ?? 'lips';
  const lipOver = kit.ground?.lipOver;
  const waterKinds: Kind[] = ['water', 'deep', ...(kit.water.also ?? [])];
  const rockAsWall = kit.rockAsWall === true && kit.wall.kind === 'props';
  const out: ViewCell[] = [];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const c = cells[y * w + x]!;
      // docs/38 BS6: no board, nothing drawn (the void layer paints the edge)
      if (c.kind === 'void') { out.push({ ground: [], overlay: [], props: [] }); continue; }
      const kind: Kind = rockAsWall && c.kind === 'rock' ? 'wall' : c.kind;
      const roll = (p: string): number => cellHash(salt, x, y, p);
      const patch = (p: { size: number; share: number } | undefined, purpose: string): boolean => !p || inPatch(salt, x, y, p.size, p.share, purpose);
      const cell: ViewCell = { ground: [], overlay: [], props: [] };
      const push = (layer: keyof ViewCell, i: number | undefined): void => { if (i !== undefined) cell[layer].push(vf(i)); };
      const isGround = groundKinds.includes(kind);
      /** Index in `cell.ground` of a frame a `fill` decal may replace (a full fill, never an edge piece). */
      let fillSlot = -1;

      // 1. base fill
      push('ground', pickVariant(kit.base, roll('base')));

      // 2. the kind
      switch (kind) {
        case 'floor':
        case 'tunnel': {
          push('ground', pickVariant(kit.floor, roll('floor')));
          if (!isGround && cell.ground.length > 1) fillSlot = cell.ground.length - 1;
          if (kind === 'tunnel') push('overlay', PROPS.traps.pit);
          break;
        }
        case 'rough': {
          if (kit.rough.kind === 'liquid') {
            const [a, b] = liquidFill(kit.rough.set);
            cell.ground.push(kit.rough.animate ? vanim(a, b) : vf(a));
            const same = mask8(grid, x, y, kindIs(['rough', 'wall'].filter((k) => k === 'rough' || kit.wall.kind === 'props') as Kind[]), MIRROR);
            for (const o of shoreOverlays(kit.rough.set, same)) push('overlay', o);
          } else {
            push('ground', pickVariant(kit.rough.frames.length ? kit.rough.frames : kit.floor, roll('rough')));
            if (kit.rough.decals && roll('roughDecal') % 1000 < (kit.rough.decalChance ?? 1) * 1000) push('props', pickVariant(kit.rough.decals, roll('roughPick')));
          }
          break;
        }
        case 'water':
        case 'deep': {
          const [a, b] = liquidFill(kit.water.set);
          cell.ground.push(kit.water.animate ? vanim(a, b) : vf(a));
          const same = mask8(grid, x, y, kindIs(waterKinds), MIRROR);
          for (const o of shoreOverlays(kit.water.set, same)) push('overlay', o);
          // deep water: the same water, darkened twice by the void dither (docs/26)
          if (kind === 'deep') { push('overlay', FILLS.voidDither); push('overlay', FILLS.voidDither); }
          break;
        }
        case 'thicket': {
          if (!isGround) push('ground', pickVariant(kit.floor, roll('floor')));
          const f = pickVariant(kit.thicket ?? THICKET, roll('thicket'));
          if (f !== undefined) cell.props.push(f === PROPS.bushDense[0] ? vanim(PROPS.bushDense[0], PROPS.bushDense[1]) : vf(f));
          // a second, lower clump so it reads as a mass, not a single bush
          push('overlay', pickVariant(THICKET_UNDER, roll('thicketUnder')));
          break;
        }
        case 'chasm': {
          // a drop: nothing but the dark, the ground's lips hanging over its rim
          cell.ground.length = 0;
          push('ground', FILLS.voidDither); push('ground', FILLS.voidDither); push('ground', FILLS.voidDither);
          // the far wall of the drop: a deep shadow under the rim wherever the floor ends to the north
          if (sample(grid, x, y - 1, NIL_EDGE, x, y) !== 'chasm') push('overlay', SHADOW.topThick);
          push('overlay', SHADOW.topHalf);
          break;
        }
        case 'wall': {
          if (kit.wall.kind === 'oryx16') {
            const m = mask8(grid, x, y, kindIs(['wall', 'door']), AS_ROCK);
            cell.ground.length = 0; // opaque piece: no base needed
            push('ground', wallPiece(kit.wall.row, m, roll('wall'), kit.wall.cracked));
            // a torch on a south-facing straight run, spaced at least three cells apart
            if (kit.wall.torches && (m & 0x0f) === (2 | 8) && x % 3 === 1 && roll('torch') % 100 < 40) cell.props.push(vanim(PROPS.torch[0], PROPS.torch[1]));
          } else {
            // props stand on the ground: draw the ground as if this were floor, then the prop
            if (!isGround) push('ground', pickVariant(kit.floor, roll('floor')));
            const wall = kit.wall;
            let frames = wall.frames;
            wall.groves?.forEach((g, i) => { if (frames === wall.frames && patch(g.patch, `grove${i}`)) frames = g.frames; });
            const f = pickVariant(frames, roll('wall'));
            if (f !== undefined) {
              const animPair = kit.wall.anim && f === PROPS.bushDense[0] ? vanim(PROPS.bushDense[0], PROPS.bushDense[1]) : vf(f);
              cell.props.push(animPair);
            }
          }
          break;
        }
        case 'rock': {
          cell.ground.length = 0;
          push('ground', pickVariant(kit.rock, roll('rock')));
          break;
        }
        case 'door': {
          cell.ground.length = 0;
          const open = c.tile.doorOpen ?? (c.tile.terrain === 'Door' && options.doorOpen ? false : false);
          push('ground', open ? kit.door.open : kit.door.closed);
          break;
        }
      }

      // 3. ground blob: one side of the boundary only (oryx.ts)
      if (kit.ground) {
        const groundMask = (): number => mask8(grid, x, y, (k) => groundKinds.includes((rockAsWall && k === 'rock' ? 'wall' : k) as Kind), MIRROR);
        if (isGround) {
          // ground kinds: the ground piece sits just above the base (below props)
          const pieces = groundEdges === 'inward' ? groundInward(kit.ground.set, groundMask(), roll('ground')) : [groundFill(kit.ground.set, roll('ground'))];
          const full = pieces.length === 1 && pieces[0]! - kit.ground.set.base <= 1;
          cell.ground.splice(1, 0, ...pieces.map(vf));
          if (full) fillSlot = 1;
        } else if (groundEdges === 'lips' && (lipOver ? lipOver.includes(kind) : true)) {
          for (const l of groundLips(kit.ground.set, groundMask())) push('overlay', l);
        }
      }

      // 4. shadows under walls
      if (kit.shadows && (kind === 'floor' || kind === 'rough' || kind === 'tunnel')) {
        const north = sample(grid, x, y - 1, AS_ROCK, x, y);
        if (north === 'wall' || north === 'rock' || north === 'door') push('overlay', SHADOW.topMid);
      }

      // 5. exits (stairs) and decals: one fill swap and one overlay/prop per cell, each the first rule that fires
      if (c.tile.exit && kit.floorExit && kind === 'floor') push('overlay', kit.floorExit.stairs);
      else if (kit.decals) {
        const fires = (d: DecalRule, i: number): boolean => {
          if (!d.on.includes(kind)) return false;
          if (roll(`decal${i}`) % 10000 >= d.chance * 10000) return false;
          if (!patch(d.cluster, `decal${i}`)) return false;
          return !!firstRule([{ match: d.match ?? {}, value: d }], grid, x, y, AS_ROCK, poses);
        };
        let fillDone = false, decorDone = false;
        for (let i = 0; i < kit.decals.length; i++) {
          const d = kit.decals[i]!;
          const isFill = d.layer === 'fill';
          if (isFill ? fillDone || fillSlot < 0 : decorDone) continue;
          if (!fires(d, i)) continue;
          const f = pickVariant(d.frames, roll(`decalPick${i}`));
          if (f === undefined) continue;
          if (isFill) { cell.ground[fillSlot] = vf(f); fillDone = true; }
          else { cell[d.layer === 'props' ? 'props' : 'overlay'].push(vf(f)); decorDone = true; }
          if (fillDone && decorDone) break;
        }
      }
      out.push(cell);
    }
  }
  return { w, h, kit: kit.key, cells: out };
}
